import { createContext, useState, useMemo, useEffect } from 'react';
import { createTheme } from '@mui/material/styles';
import { themeColors } from '../theme/colors';

// Re-export themeColors per componenti che lo importano da qui
export { themeColors };

// Crea il context
export const ColorModeContext = createContext({ 
  toggleColorMode: () => {},
  setMode: () => {},
  mode: 'dark' 
});

// Provider del tema
export const ColorModeContextProvider = ({ children }) => {
  // Controlla se c'è una preferenza salvata nel localStorage
  const storedMode = localStorage.getItem('themeMode') || 'dark';
  const [mode, setMode] = useState(storedMode);
  
  // Salva la preferenza nel localStorage quando cambia
  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);
  
  // Funzione per invertire il tema
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
      setMode: (newMode) => {
        setMode(newMode);
      },
      mode,
    }),
    [mode],
  );
  
  // Definizione dei temi disponibili
  const theme = useMemo(
    () => {
      const colors = themeColors[mode] || themeColors.light;
      const isDark = mode === 'dark';
      const isBrown = mode === 'brown';
      const isSoultech = mode === 'soultech';
      
      const baseTheme = {
        palette: {
          mode: isDark ? 'dark' : 'light',
          primary: colors.primary,
          secondary: colors.secondary,
          background: colors.background,
          text: colors.text,
          divider: colors.divider,
          action: colors.action,
          error: colors.error,
          warning: colors.warning,
          info: colors.info,
          success: colors.success,
          ...(isSoultech && { accent: colors.accent }),
        },
        // ========================================
        // COMPONENT OVERRIDES - Stili globali
        // Effetti professionali uniformi
        // ========================================
        shape: {
          borderRadius: 8,  // Bordi arrotondati uniformi
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                },
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backdropFilter: 'blur(20px)',
                backgroundColor: isDark 
                  ? 'rgba(18, 18, 18, 0.95)' 
                  : isBrown 
                    ? 'rgba(93, 64, 55, 0.95)'
                    : isSoultech
                      ? '#264653'  // Blu petrolio solido per SoulTech
                      : 'rgba(255, 255, 255, 0.95)',
                background: isSoultech 
                  ? 'linear-gradient(135deg, #1A323B 0%, #264653 50%, #2A4F5F 100%)'
                  : undefined,
                // Colore testo per tema light (sfondo bianco = testo scuro)
                color: isDark 
                  ? colors.text.primary 
                  : isBrown 
                    ? '#FFFFFF'
                    : isSoultech
                      ? '#FFFFFF'
                      : colors.text.primary,  // Testo scuro su sfondo bianco
                borderBottom: isSoultech 
                  ? '1px solid rgba(42, 157, 143, 0.3)'
                  : `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                boxShadow: isSoultech 
                  ? '0 4px 24px rgba(26, 50, 59, 0.25)'
                  : '0 4px 20px rgba(0, 0, 0, 0.08)',
                borderRadius: 0,  // Nessuna stondatura
              },
            },
          },
          MuiButton: {
            defaultProps: {
              variant: 'contained',
              disableElevation: false,
            },
            styleOverrides: {
              root: {
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 8,
                padding: '10px 24px',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
              },
              contained: {
                '&.MuiButton-containedPrimary': {
                  color: colors.primary.contrastText,
                  background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${colors.primary.light} 0%, ${colors.primary.main} 100%)`,
                  },
                },
                '&.MuiButton-containedSecondary': {
                  color: colors.secondary.contrastText,
                  background: `linear-gradient(135deg, ${colors.secondary.main} 0%, ${colors.secondary.dark} 100%)`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${colors.secondary.light} 0%, ${colors.secondary.main} 100%)`,
                  },
                },
              },
              outlined: {
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                },
              },
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: colors.action.hover,
                  transform: 'scale(1.1)',
                },
              },
            },
          },
          MuiChip: {
            defaultProps: {
              variant: 'filled',
            },
            styleOverrides: {
              root: {
                fontWeight: 500,
                borderRadius: 6,
                transition: 'all 0.2s ease',
              },
              filled: {
                '&.MuiChip-colorPrimary': {
                  color: colors.primary.contrastText,
                },
                '&.MuiChip-colorSecondary': {
                  color: colors.secondary.contrastText,
                },
              },
            },
          },
          MuiFab: {
            styleOverrides: {
              root: {
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
                '&.MuiFab-primary': {
                  color: colors.primary.contrastText,
                  background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
                },
                '&.MuiFab-secondary': {
                  color: colors.secondary.contrastText,
                  background: `linear-gradient(135deg, ${colors.secondary.main} 0%, ${colors.secondary.dark} 100%)`,
                },
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 8,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: isDark 
                    ? '0 12px 40px rgba(0,0,0,0.4)' 
                    : '0 12px 40px rgba(0,0,0,0.12)',
                },
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
                borderRadius: 8,
              },
              elevation1: {
                boxShadow: isDark 
                  ? '0 2px 8px rgba(0,0,0,0.3)' 
                  : '0 2px 8px rgba(0,0,0,0.08)',
              },
              elevation2: {
                boxShadow: isDark 
                  ? '0 4px 16px rgba(0,0,0,0.35)' 
                  : '0 4px 16px rgba(0,0,0,0.1)',
              },
              elevation3: {
                boxShadow: isDark 
                  ? '0 8px 24px rgba(0,0,0,0.4)' 
                  : '0 8px 24px rgba(0,0,0,0.12)',
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                fontSize: '0.875rem',  // 14px - uniforme
                padding: '12px 16px',
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
              },
              head: {
                fontWeight: 700,
                fontSize: '0.8125rem',  // 13px - leggermente più piccolo
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                color: colors.text.secondary,
              },
              body: {
                color: colors.text.primary,
              },
            },
          },
          MuiTableRow: {
            styleOverrides: {
              root: {
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                },
                '&:nth-of-type(even)': {
                  backgroundColor: isDark ? 'rgba(255,255,255,0.015)' : 'rgba(0,0,0,0.01)',
                },
              },
            },
          },
          MuiTableHead: {
            styleOverrides: {
              root: {
                '& .MuiTableCell-head': {
                  borderBottom: `2px solid ${colors.primary.main}`,
                },
              },
            },
          },
          MuiTableContainer: {
            styleOverrides: {
              root: {
                borderRadius: 8,
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
              },
            },
          },
          // ========================================
          // DRAWER / SIDEBAR STYLES
          // Icone e voci menu professionali
          // ========================================
          MuiListItemButton: {
            styleOverrides: {
              root: {
                borderRadius: 6,
                margin: '2px 6px',
                padding: '8px 12px',
                minHeight: 44,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: isDark 
                    ? 'rgba(255,255,255,0.08)' 
                    : isBrown 
                      ? 'rgba(255,255,255,0.1)'
                      : isSoultech
                        ? 'rgba(255,255,255,0.08)'
                        : 'rgba(0,0,0,0.04)',
                  transform: 'translateX(2px)',
                },
                '&.Mui-selected': {
                  backgroundColor: `${colors.primary.main}15`,
                  borderLeft: `3px solid ${colors.primary.main}`,
                  '&:hover': {
                    backgroundColor: `${colors.primary.main}25`,
                  },
                  '& .MuiListItemIcon-root': {
                    color: colors.primary.main,
                  },
                  '& .MuiListItemText-primary': {
                    color: colors.primary.main,
                    fontWeight: 600,
                  },
                },
              },
            },
          },
          MuiListItemIcon: {
            styleOverrides: {
              root: {
                minWidth: 36,
                color: colors.text.secondary,
                transition: 'all 0.2s ease',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.25rem',
                },
              },
            },
          },
          MuiListItemText: {
            styleOverrides: {
              primary: {
                fontSize: '0.875rem',
                fontWeight: 500,
                letterSpacing: '0.1px',
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                borderRight: 'none',
                boxShadow: isDark 
                  ? '4px 0 24px rgba(0,0,0,0.3)' 
                  : '4px 0 24px rgba(0,0,0,0.08)',
              },
            },
          },
          MuiTextField: {
            defaultProps: {
              variant: 'outlined',
            },
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: 8,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  },
                  '&.Mui-focused': {
                    boxShadow: `0 0 0 3px ${colors.primary.main}25`,
                  },
                },
              },
            },
          },
          MuiSelect: {
            defaultProps: {
              variant: 'outlined',
            },
          },
          MuiDialog: {
            styleOverrides: {
              paper: {
                borderRadius: 12,
                boxShadow: '0 24px 80px rgba(0,0,0,0.25)',
              },
            },
          },
          MuiMenu: {
            styleOverrides: {
              paper: {
                borderRadius: 8,
                marginTop: 8,
                boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
              },
            },
          },
          MuiMenuItem: {
            styleOverrides: {
              root: {
                borderRadius: 6,
                margin: '4px 8px',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                },
              },
            },
          },
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                borderRadius: 6,
                padding: '8px 12px',
                fontSize: '0.8rem',
                backdropFilter: 'blur(10px)',
                backgroundColor: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(33,33,33,0.9)',
                color: isDark ? '#333' : '#fff',
              },
            },
          },
          MuiAvatar: {
            styleOverrides: {
              root: {
                border: `2px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
              },
            },
          },
          MuiTabs: {
            styleOverrides: {
              indicator: {
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                fontWeight: 600,
                transition: 'all 0.2s ease',
                '&.Mui-selected': {
                  color: colors.primary.main,
                },
              },
            },
          },
          MuiAlert: {
            styleOverrides: {
              root: {
                borderRadius: 12,
              },
              standardSuccess: {
                backgroundColor: isDark ? 'rgba(46, 125, 50, 0.15)' : 'rgba(46, 125, 50, 0.1)',
              },
              standardError: {
                backgroundColor: isDark ? 'rgba(211, 47, 47, 0.15)' : 'rgba(211, 47, 47, 0.1)',
              },
              standardWarning: {
                backgroundColor: isDark ? 'rgba(237, 108, 2, 0.15)' : 'rgba(237, 108, 2, 0.1)',
              },
              standardInfo: {
                backgroundColor: isDark ? 'rgba(2, 136, 209, 0.15)' : 'rgba(2, 136, 209, 0.1)',
              },
            },
          },
          MuiLinearProgress: {
            styleOverrides: {
              root: {
                borderRadius: 4,
                height: 6,
              },
            },
          },
          MuiSwitch: {
            styleOverrides: {
              root: {
                '& .MuiSwitch-switchBase.Mui-checked': {
                  '& + .MuiSwitch-track': {
                    backgroundColor: colors.primary.main,
                  },
                },
              },
            },
          },
        },
      };
      
      // Aggiungi shadows personalizzate per temi non-dark
      if (isBrown) {
        baseTheme.shadows = [
          'none',
          '0px 2px 1px -1px rgba(93,64,55,0.2),0px 1px 1px 0px rgba(93,64,55,0.14),0px 1px 3px 0px rgba(93,64,55,0.12)',
          '0px 3px 1px -2px rgba(93,64,55,0.2),0px 2px 2px 0px rgba(93,64,55,0.14),0px 1px 5px 0px rgba(93,64,55,0.12)',
          '0px 3px 3px -2px rgba(93,64,55,0.2),0px 3px 4px 0px rgba(93,64,55,0.14),0px 1px 8px 0px rgba(93,64,55,0.12)',
          '0px 2px 4px -1px rgba(93,64,55,0.2),0px 4px 5px 0px rgba(93,64,55,0.14),0px 1px 10px 0px rgba(93,64,55,0.12)',
          ...Array(20).fill('0px 2px 4px -1px rgba(93,64,55,0.2),0px 4px 5px 0px rgba(93,64,55,0.14),0px 1px 10px 0px rgba(93,64,55,0.12)')
        ];
      } else if (isSoultech) {
        baseTheme.shadows = [
          'none',
          '0px 2px 1px -1px rgba(38,42,44,0.2),0px 1px 1px 0px rgba(38,42,44,0.14),0px 1px 3px 0px rgba(38,42,44,0.12)',
          '0px 3px 1px -2px rgba(38,42,44,0.2),0px 2px 2px 0px rgba(38,42,44,0.14),0px 1px 5px 0px rgba(38,42,44,0.12)',
          '0px 3px 3px -2px rgba(38,42,44,0.2),0px 3px 4px 0px rgba(38,42,44,0.14),0px 1px 8px 0px rgba(38,42,44,0.12)',
          '0px 2px 4px -1px rgba(38,42,44,0.2),0px 4px 5px 0px rgba(38,42,44,0.14),0px 1px 10px 0px rgba(38,42,44,0.12)',
          ...Array(20).fill('0px 2px 4px -1px rgba(38,42,44,0.2),0px 4px 5px 0px rgba(38,42,44,0.14),0px 1px 10px 0px rgba(38,42,44,0.12)')
        ];
      }
      
      return createTheme(baseTheme);
    },
    [mode],
  );
  
  return (
    <ColorModeContext.Provider value={colorMode}>
      {children(mode, colorMode.setMode, theme)}
    </ColorModeContext.Provider>
  );
};

